import { useState } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { Armchair, CheckCircle, Loader2, AlertCircle, Search, Clock, ChefHat, Package } from 'lucide-react';
import { useOrders } from '../../hooks/useOrders';
import { useTables } from '../../hooks/useTables';
import { useAuth } from '../../contexts/AuthContext';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { toast } from 'sonner';
import { TableOrdersModal } from './TableOrdersModal';
// import { Order } from '../../services/orders.service';

interface TablesPageProps {
  onNavigate?: (page: string) => void;
  onTableSelect?: (tableId: string) => void;
  menuTypeId?: string;
}

export function TablesPage({ onNavigate, /*onTableSelect,*/ menuTypeId }: TablesPageProps) {
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTableForOrders, setSelectedTableForOrders] = useState<{ id: string; name: string } | null>(null);

  const { user } = useAuth();
  const { orders: ordersData, refresh: refreshOrders } = useOrders({ page: 1, limit: 1000 });
  const { currentBranch, setSelectedTable } = useRestaurant();
  
  // Try fetching without filters first to debug
  const { tables, loading, error } = useTables();

  // Debug logging
  console.log('TablesPage Debug:', {
    userId: user?.id,
    restaurantId: user?.restaurantId,
    currentBranch: currentBranch,
    branchId: currentBranch?.id,
    tablesCount: tables.length,
    tables: tables,
    loading,
    error
  });

  // Get the selected menu type ID from localStorage if not provided as prop
  const selectedMenuTypeId = menuTypeId || localStorage.getItem('selectedMenuTypeId') || '';

  console.log('TablesPage - selectedMenuTypeId:', selectedMenuTypeId);

  // Helper function to get table orders and filter out closed, cancelled, rejected, and refunded orders
  // DELIVERED orders should still be visible and keep table occupied until payment is made
  const getTableOrders = (tableName: string) => {
    return ordersData.filter(order => {
      const orderTable = order.notes?.match(/Table:\s*(.+)/)?.[1]?.trim();
      return orderTable === tableName && 
             order.status !== 'CLOSED' && 
             order.status !== 'CANCELLED' && 
             order.status !== 'REJECTED' &&
             order.status !== 'REFUNDED';
    });
  };

  // Helper function to get table status based on orders
  const getTableStatus = (tableName: string) => {
    const tableOrders = getTableOrders(tableName);
    if (tableOrders.length === 0) return null;

    // Priority: PENDING > CONFIRMED > PREPARING > READY
    const statuses = tableOrders.map(o => o.status);
    if (statuses.includes('PENDING')) return 'PENDING';
    if (statuses.includes('CONFIRMED')) return 'CONFIRMED';
    if (statuses.includes('PREPARING')) return 'PREPARING';
    if (statuses.includes('READY')) return 'READY';
    return null;
  };

  // Status badge configuration
  const statusBadgeConfig = {
    PENDING: { label: 'New', color: 'bg-orange-500', icon: Clock },
    CONFIRMED: { label: 'Accepted', color: 'bg-blue-500', icon: CheckCircle },
    PREPARING: { label: 'Preparing', color: 'bg-purple-500', icon: ChefHat },
    READY: { label: 'Ready', color: 'bg-green-500', icon: Package },
  };

  // Sort tables by numeric value in name, then filter by search query
  const filteredTables = tables
    .filter(table => table.isActive)
    .sort((a, b) => {
      // Extract numbers from table names (e.g., "Table 1" -> 1, "Table 10" -> 10)
      const numA = parseInt(a.name.match(/\d+/)?.[0] || '0');
      const numB = parseInt(b.name.match(/\d+/)?.[0] || '0');
      return numA - numB;
    })
    .filter(table =>
      table.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

  const handleTableSelect = (tableId: string, tableName: string) => {
    setSelectedTable(tableId);
    toast.success(`Table ${tableName} selected`);
    
    // After selecting table, navigate to menus with the menu type ID
    if (selectedMenuTypeId) {
      // Store the menu type ID for the menus page to use
      localStorage.setItem('selectedMenuTypeId', selectedMenuTypeId);
      onNavigate?.('menus');
    } else {
      // Fallback: navigate back to menu types if no menu type was selected
      onNavigate?.('menuTypes');
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(!sidebarOpen)}
        activePage="menus"
        onNavigate={onNavigate}
        ordersCount={ordersData.length}
      />

      <div
        className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
          sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'
        }`}
      >
        <TopBar
          onMenuClick={() => setSidebarOpen(!sidebarOpen)}
          sidebarOpen={sidebarOpen}
          onNavigate={onNavigate}
        />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">
            {/* Page Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Select a Table</h1>
              <p className="text-gray-400 dark:text-gray-500 mt-1 text-sm">
                Choose a table to place your order
              </p>
            </div>

            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder="Search tables..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Loader2 className="w-12 h-12 text-indigo-600 dark:text-indigo-400 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">Loading tables...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-red-900 dark:text-red-200 mb-1">Error Loading Tables</h3>
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && filteredTables.length === 0 && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Armchair className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Tables Available</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {searchQuery ? 'No tables match your search.' : 'There are no active tables. Please contact staff.'}
                </p>
              </div>
            )}

            {/* Tables Grid */}
            {!loading && !error && filteredTables.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {filteredTables.map((table) => {
                  const tableStatus = getTableStatus(table.name);
                  const tableOrders = getTableOrders(table.name);
                  const hasActiveOrders = tableOrders.length > 0;
                  const statusConfig = tableStatus ? statusBadgeConfig[tableStatus as keyof typeof statusBadgeConfig] : null;
                  const StatusIcon = statusConfig?.icon;

                  return (
                    <div
                      key={table.id}
                      className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-sm border-2 border-gray-200 dark:border-gray-800 hover:shadow-lg transition-all overflow-hidden"
                    >
                      {/* Status Badge */}
                      {statusConfig && (
                        <div className={`absolute top-0 left-0 right-0 ${statusConfig.color} text-white text-xs font-semibold px-3 py-1.5 flex items-center justify-center gap-1.5 z-10`}>
                          {StatusIcon && <StatusIcon className="w-3.5 h-3.5" />}
                          {statusConfig.label}
                        </div>
                      )}

                      {/* Table Content */}
                      <div className={`p-6 flex flex-col items-center justify-center gap-3 ${statusConfig ? 'pt-12' : ''}`}>
                        {/* Table Icon */}
                        <div className={`w-16 h-16 rounded-xl flex items-center justify-center transition-transform ${
                          hasActiveOrders 
                            ? 'bg-gradient-to-br from-orange-500 to-red-600' 
                            : 'bg-gradient-to-br from-indigo-500 to-purple-600'
                        }`}>
                          <Armchair className="w-8 h-8 text-white" />
                        </div>

                        {/* Table Info */}
                        <div className="text-center">
                          <h3 className="text-base font-bold text-gray-900 dark:text-white">
                            {table.name}
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {table.capacity} {table.capacity === 1 ? 'seat' : 'seats'}
                          </p>
                          {hasActiveOrders && (
                            <p className="text-xs font-semibold text-orange-600 dark:text-orange-400 mt-1">
                              {tableOrders.length} active {tableOrders.length === 1 ? 'order' : 'orders'}
                            </p>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col gap-2 w-full mt-2">
                          {hasActiveOrders && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTableForOrders({ id: table.id, name: table.name });
                              }}
                              className="w-full py-2 px-3 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition-colors"
                            >
                              Open
                            </button>
                          )}
                          <button
                            onClick={() => handleTableSelect(table.id, table.name)}
                            className={`w-full py-2 px-3 text-sm font-semibold rounded-lg transition-colors ${
                              hasActiveOrders
                                ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                            }`}
                          >
                            {hasActiveOrders ? 'Add Order' : 'Select'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Info Box */}
            {!loading && !error && filteredTables.length > 0 && (
              <div className="mt-8 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-4 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-indigo-900 dark:text-indigo-200 mb-1">
                    Select Your Table
                  </h3>
                  <p className="text-sm text-indigo-700 dark:text-indigo-300">
                    Click on any table to start placing your order. Your selection will be saved for this session.
                  </p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Table Orders Modal */}
      {selectedTableForOrders && (
        <TableOrdersModal
          isOpen={!!selectedTableForOrders}
          onClose={() => setSelectedTableForOrders(null)}
          tableName={selectedTableForOrders.name}
          orders={getTableOrders(selectedTableForOrders.name)}
          onOrdersUpdate={() => {
            refreshOrders();
            setSelectedTableForOrders(null);
          }}
        />
      )}
    </div>
  );
}